import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  AfterViewInit,
  ViewChild,
  ElementRef
} from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { AppConfigService } from '../../services/app-config.service';
import { FilterUIType, ODataFilter } from './models/odata-filter';
import { SelectedFilter } from './models/selected-filter';
import { ApiVersions } from '../../services/api-version';
import { SnackBarService } from '../../services/snackbar.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { CustomViewDisplayLayout } from './models/custom-view-display-layout';
import { FilterMenuOption } from './filter-menu-option';
import { CustomView } from './models/custom-view';
import { AuthService } from 'src/app/auth/auth.service';
import {
  ModalConfirmationComponent,
  ModalConfirmationModel
} from '../../../../shared-app-portal/components/modal-confirmation/modal-confirmation.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'odata-filter',
  templateUrl: './odata-filter.component.html',
  styleUrls: ['./odata-filter.component.scss'],
  standalone: false
})
export class ODataFilterComponent implements OnInit, OnDestroy, AfterViewInit {
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.onCancel();
    }
  }
  customViewDisplayLayoutEnum = CustomViewDisplayLayout;
  filters: ODataFilter[];
  showPanel = false;
  selectedFilters: SelectedFilter[] = [];
  appliedFilters: SelectedFilter[] = [];
  subs: Subscription[] = [];
  smallSize = false;
  midSize = false;
  isAdvancedViewMode = false;
  @ViewChild('filterComponent') filterComponent: ElementRef;
  @Input() name: string = '';
  @Input() filterMenuOption: Partial<FilterMenuOption> = {};
  @Input() customViewDisplayLayout: CustomViewDisplayLayout;
  @Output() manageColumns = new EventEmitter();
  @Output() createView = new EventEmitter<CustomView>();
  @Output() selectView = new EventEmitter<CustomView>();
  @Output() editView = new EventEmitter();
  @Output() displayLayoutChanged = new EventEmitter<CustomViewDisplayLayout>();
  @Output() aiModeClick = new EventEmitter();

  get filterButtonText(): string {
    let filterCount = this.appliedFilters.length;
    if (filterCount == 0) return `${this.name} Filters`;
    if (filterCount == 1) return `${this.name} Filter`;
    if (filterCount > 1) return `${this.name} Filters`;
  }

  get showDisplayLayoutOptions() {
    return (
      this.filterMenuOption.showListToggleButton ||
      this.filterMenuOption.showChartToggleButton ||
      this.filterMenuOption.showTableToggleButton
    );
  }

  get selectedView() {
    return this.filterMenuOption?.filterService?.selectedView;
  }
  get searchText() {
    return this.filterMenuOption?.filterService?.searchText;
  }

  /** sets filterMenuOption.filterService.searchText */
  set searchText(value: string) {
    this.filterMenuOption.filterService.searchText = value;
  }

  constructor(
    private appConfigService: AppConfigService,
    private snackBarService: SnackBarService,
    private clipboard: Clipboard,
    private authService: AuthService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (!this.filterMenuOption.filterService) return;
    this.subs.push(
      combineLatest([
        this.filterMenuOption.filterService.appliedFilters,
        this.filterMenuOption.filterService.filterCollection
      ]).subscribe((results) => {
        if (results[0] && results[1]) {
          this.updateFilters(results);
        }
      })
    );
  }

  ngAfterViewInit() {
    this.triggerFilterComponentSize();
  }

  triggerFilterComponentSize() {
    if (this.filterMenuOption.hideSearchBar) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      if (entries[0].contentRect.width <= 961) {
        this.midSize = true;
      } else {
        this.midSize = false;
      }
      if (entries[0].contentRect.width <= 800) {
        this.smallSize = true;
      } else {
        this.smallSize = false;
      }
    });
    observer.observe(this.filterComponent.nativeElement);
  }

  search(searchText: string) {
    this.searchText = searchText;
    this.filterMenuOption.filterService.applyFilter(this.appliedFilters);
  }
  onSwitchMode() {
    this.isAdvancedViewMode = !this.isAdvancedViewMode;
  }
  updateFilters(results: [appliedFilters: SelectedFilter[], filterCollection: ODataFilter[]]) {
    this.filters = results[1];
    this.appliedFilters = results[0];
    this.selectedFilters = this.appliedFilters.map((appliedFilter) => {
      let filter = this.filters.find(
        (x) =>
          x.displayName == appliedFilter.filter.displayName &&
          x.targetField == appliedFilter.filter.targetField &&
          x.filterGroup == appliedFilter.filter.filterGroup &&
          x.childFieldName == appliedFilter.filter.childFieldName
      );
      appliedFilter.filter = filter;
      return new SelectedFilter(appliedFilter);
    });
    this.isAdvancedViewMode = this.selectedView?.isAdvanced ?? this.isAdvancedViewMode;
  }

  addFilter() {
    this.selectedFilters.push(new SelectedFilter(null));
  }

  deleteFilter(index: number) {
    this.selectedFilters.splice(index, 1);
  }

  deleteSubFilter(selectedFilter: SelectedFilter, index: number) {
    selectedFilter.selectedSubFilters.splice(index, 1);
  }
  private applyFilters() {
    this.filterMenuOption.filterService.isFetchingData = true;
    this.filterMenuOption.filterService.applyFilter(this.appliedFilters);
    if (this.filterMenuOption.filterService.selectedView) {
      this.filterMenuOption.filterService.selectedView.isModified = true;
    }
    this.showPanel = false;
  }

  onApply() {
    if (this.isAdvancedViewMode) {
      this.selectedView.isAdvanced = true;
      this.filterMenuOption.filterService.isFetchingData = true;
      this.filterMenuOption.filterService.applyQueryCode(this.selectedView.oDataQuery);
      if (this.filterMenuOption.filterService.selectedView) {
        this.filterMenuOption.filterService.selectedView.isModified = true;
        this.filterMenuOption.filterService.selectedView.isAdvanced = true;
      }
      this.showPanel = false;
      return;
    }
    if (this.selectedView && this.filterMenuOption.filterService.selectedView) {
      this.selectedView.oDataQuery = null;
      this.selectedView.isAdvanced = false;
      this.filterMenuOption.filterService.selectedView.isAdvanced = false;
      this.filterMenuOption.filterService.selectedView.oDataQuery = null;
    }
    this.selectedFilters.forEach((item) => {
      item.isFilterApplied = true;
      this.cleanupFiltersBeforApply(item);
      if (item.hasSubFilter && item.isSubFilterAdded) {
        item.selectedSubFilters.forEach((subItem) => {
          subItem.isFilterApplied = true;
          this.cleanupFiltersBeforApply(subItem);
        });
      }
    });

    if (!this.isValid) return;

    this.appliedFilters = this.acceptableFilters.map((x) => new SelectedFilter(x));
    this.applyFilters();
  }
  onCancel() {
    this.showPanel = false;
  }

  onCopyODataFilter(): void {
    // check if filters are valid; when actually filtering, there's a guard to check for this, so we need to process this here
    const filters = this.acceptableFilters.map((x) => new SelectedFilter(x));
    filters.forEach((item) => {
      // to make sure we do not send any value if filter uses an operator that does not need value.
      this.cleanupFiltersBeforApply(item);
      if (item.hasSubFilter && item.isSubFilterAdded) {
        item.selectedSubFilters.forEach((subItem) => {
          this.cleanupFiltersBeforApply(subItem);
        });
      }
    });

    this.clipboard.copy(
      this.filterMenuOption.filterService.getODataEndpoint(filters, '/api', ApiVersions.v1_0)
    );
    this.snackBarService.open('Search query copied to your clipboard!', true);
  }

  onOpen() {
    if (this.showPanel) {
      this.showPanel = false;
      return;
    }
    this.selectedFilters = this.appliedFilters.map((x) => new SelectedFilter(x));
    if (this.selectedView?.isAdvanced) {
      this.isAdvancedViewMode = true;
    } else {
      this.isAdvancedViewMode = false;
    }
    this.showPanel = true;
  }

  clearFilters() {
    this.selectedFilters = [];
    this.appliedFilters = [];
  }

  clearAndApply() {
    this.clearFilters();
    this.applyFilters();
  }

  onSelectView(view: CustomView) {
    this.onCancel();
    if (this.selectedView?.isAdvanced) {
      this.isAdvancedViewMode = true;
    } else {
      this.isAdvancedViewMode = false;
    }
    if (view) {
      this.selectView.emit(view);
    }
  }
  onCreateView() {
    this.onCancel();
    this.createView.emit();
  }

  onEditView(view: CustomView) {
    this.onCancel();
    this.editView.emit(view);
  }
  onSaveViewChange() {
    if (!this.canSaveChanges) return;
    let data: ModalConfirmationModel = {
      simpleDialog: false,
      title: null,
      action: `save the change for ${this.selectedView.name}`,
      delete: false,
      okButtonText: 'Save Changes',
      okButtonId: 'modal-odata-filter',
      description: null,
      textMatcherValue: ''
    };
    let confirmationRef = this.dialog.open(ModalConfirmationComponent, {
      data: data
    });

    confirmationRef.afterClosed().subscribe((result) => {
      if (result) {
        this.filterMenuOption.filterService.saveView();
      }
    });
  }

  addSubFilter(selectedFilter: SelectedFilter) {
    selectedFilter.selectedSubFilters.push(new SelectedFilter(null));
  }

  get acceptableFilters(): SelectedFilter[] {
    return this.selectedFilters.filter((x) => x.isValid);
  }

  get isValid(): boolean {
    return this.selectedFilters.findIndex((x) => !x.isValid || !x.isSubFilterValid) == -1;
  }

  get isApplied(): boolean {
    return this.selectedFilters.findIndex((x) => !x.isFilterApplied || !x.isSubFilerApplied) == -1;
  }
  get showFieldDeletedWarning() {
    return this.appliedFilters.findIndex((x) => x.showFieldDeletedWarning) != -1;
  }

  get doesPlanAllowManageColumns(): boolean {
    return this.appConfigService.tenant.plan.canManageColumns;
  }

  get canSaveChanges(): boolean {
    return (
      !this.selectedView.isSystemView &&
      (this.authService.ownerLevelAccess ||
        this.selectedView.auth0UserId == this.authService.userProfile.userId)
    );
  }

  private cleanupFiltersBeforApply(item: SelectedFilter) {
    //to make sure we do not send any value if filter uses an operator that does not need value.
    if (item.filterOperator && !item.filterOperator.isSearchValueNeeded) {
      item.searchValue = null;
      item.searchValues = [];
    }
    if (item.uiType != FilterUIType.Multiselect) {
      item.searchValues = [];
    } else {
      item.searchValue = null;
    }
  }
  ngOnDestroy(): void {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
